import PropTypes from 'prop-types';
import compose from "ramda/es/compose";
import find from "ramda/es/find";
import prop from "ramda/es/prop";
import propEq from "ramda/es/propEq";
import defaultToEmptyObject from "ramda-extension/es/defaultToEmptyObject";
import React from 'react';
import { getMessage } from '@gef-ui/localization/messages';
import Popper from '@material-ui/core/Popper';
import useAutocomplete from '@material-ui/lab/useAutocomplete';
import FormGroup from '../../../molecules/FormGroup';
import { validationDisplayTypePropType } from '../../../molecules/Validation/propTypes';
import { ListBox } from '../../../organisms/ListBox';
import { useDefaultComponentValue } from '../../../utils/useDefaultComponentValue';
import SelectInput from '../../Select/SelectInput';
import SelectOption from '../../Select/SelectOption';
import SelectPanelControls from '../../Select/SelectPanelControls';
import AngleDown from '../../SvgIcon/Icons/AngleDown';
import AngleUp from '../../SvgIcon/Icons/AngleUp';
const findByIndex = (index, errors)=>compose(prop('errorMessage'), defaultToEmptyObject, find(propEq(index, 'index')))(errors);
const COMPONENT_NAME = 'MuiAutocomplete';
const stripDiacritics = (inputValue)=>inputValue.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
const defaultFreeSoloFilter = (value)=>(options, { getOptionLabel })=>{
        const input = stripDiacritics(value);
        return options.filter((option)=>{
            const candidate = stripDiacritics(getOptionLabel(option));
            return candidate.indexOf(input) > -1;
        });
    };
const MuiAutocomplete = (inProps)=>{
    const props = useDefaultComponentValue({
        inProps,
        name: COMPONENT_NAME
    });
    const { outline, tooltip, additionalInputElement, floatingLabelText, floatingLabelClassName, id: propId, controlClassName, controlInputClassName, validationText, validationClassName, disabled, readOnly, readOnlyInput, placeholder, asyncValidationInProgress, multiple, labelText, labelNote, labelPosition, labelClassName, maxLength, sizeSmall, tabIndex, required, validationDisplayType, autoFocus, controlPanelIconOpen = /*#__PURE__*/ React.createElement(AngleDown, null), controlPanelIconClose = /*#__PURE__*/ React.createElement(AngleUp, null), clearable, additionalPanelControlElement, options, freeSolo, getOptionDisabled, groupBy, getOptionLabel = (option)=>option.label || option, renderOption = (optionProps, _option, optionLabel)=>/*#__PURE__*/ React.createElement(SelectOption, optionProps, optionLabel), value: propValue, inputValue, filterOptions = freeSolo ? defaultFreeSoloFilter(multiple ? inputValue : propValue) : undefined, onChange, onClose, onInputChange, onOpen, onBlur, onFocus, blurOnSelect, openOnFocus, optionsTargetContainer, selectOnFocus = false, filterSelectedOptions = true, errMeta = [], TextFieldProps = {}, ListboxProps = {}, ListboxComponent = ListBox, getOptionSelected, noOptionsText = getMessage('common:noRecord') } = props;
    const { id, getRootProps, getInputProps, getTagProps, getPopupIndicatorProps, getListboxProps, getOptionProps, groupedOptions, getClearProps, value, setAnchorEl, anchorEl, popupOpen } = useAutocomplete({
        openOnFocus,
        blurOnSelect,
        onClose,
        onInputChange,
        onOpen,
        getOptionLabel,
        groupBy,
        filterOptions,
        getOptionDisabled,
        freeSolo,
        id: propId,
        inputValue,
        getOptionSelected,
        multiple,
        selectOnFocus,
        filterSelectedOptions,
        options,
        value: propValue,
        onChange
    });
    const muiInputProps = getInputProps();
    const panelProps = {
        controlPanelIconOpen,
        controlPanelIconClose,
        clearable,
        isOpen: popupOpen,
        value,
        disabled,
        readOnly,
        onSelect: getPopupIndicatorProps().onClick,
        onClear: getClearProps().onClick,
        additionalPanelControlElement
    };
    const selectInputProps = {
        inputWrapperProps: {
            ref: setAnchorEl
        },
        labelText,
        labelNote,
        labelPosition,
        labelClassName,
        additionalInputElement,
        id,
        autoFocus,
        floatingLabelText,
        floatingLabelClassName,
        controlClassName,
        controlInputClassName,
        validationText,
        validationClassName,
        disabled,
        readOnly,
        readOnlyInput,
        placeholder,
        asyncValidationInProgress,
        multiSelect: multiple,
        simple: true,
        maxLength,
        sizeSmall,
        tabIndex,
        panelControls: /*#__PURE__*/ React.createElement(SelectPanelControls, panelProps),
        value: muiInputProps.value,
        pills: multiple && value.map((option, index)=>{
            const { onDelete, ...rest } = getTagProps({
                index
            });
            return {
                label: getOptionLabel(option),
                onClick: onDelete,
                ...rest,
                validationText: findByIndex(index, errMeta),
                validationClassName
            };
        }),
        outline,
        tooltip,
        required,
        validationDisplayType,
        ...muiInputProps,
        ...TextFieldProps,
        onFocus: (...args)=>{
            muiInputProps.onFocus(...args);
            onFocus?.(...args);
        },
        onBlur: (...args)=>{
            muiInputProps.onBlur(...args);
            onBlur?.(...args);
        },
        onChange: (...args)=>{
            muiInputProps.onChange(...args);
            TextFieldProps?.onChange?.(...args);
        },
        inputControlRef: muiInputProps.ref
    };
    if (TextFieldProps?.inputRef) {
        TextFieldProps.inputRef.current = muiInputProps.ref?.current;
    }
    return /*#__PURE__*/ React.createElement("div", {
        className: "Select",
        ...getRootProps()
    }, /*#__PURE__*/ React.createElement(SelectInput, selectInputProps), popupOpen && anchorEl ? /*#__PURE__*/ React.createElement(Popper, {
        open: true,
        style: {
            width: anchorEl ? anchorEl.clientWidth : null
        },
        className: "Select-popper",
        role: "presentation",
        anchorEl: anchorEl,
        container: optionsTargetContainer
    }, /*#__PURE__*/ React.createElement(ListboxComponent, {
        ...ListboxProps,
        ...getListboxProps(),
        className: "Select-list"
    }, groupedOptions.map((option, index)=>{
        const listBoxProps = getOptionProps({
            option,
            index
        });
        return renderOption({
            ...listBoxProps,
            selected: listBoxProps['aria-selected']
        }, option, getOptionLabel(option));
    }), groupedOptions.length === 0 && /*#__PURE__*/ React.createElement(SelectOption, {
        className: "Select-noOptions",
        disabled: true
    }, noOptionsText))) : null);
};
MuiAutocomplete.propTypes = {
    ListboxComponent: PropTypes.elementType,
    ListboxProps: PropTypes.object,
    /**
	 * Props of textfield
	 */ TextFieldProps: PropTypes.object,
    /**
	 * Element rendered in select input after real input
	 */ additionalInputElement: PropTypes.node,
    /**
	 * additionalPanelControlElement
	 */ additionalPanelControlElement: PropTypes.node,
    /**
	 * True if async validation is in progress
	 */ asyncValidationInProgress: PropTypes.bool,
    /**
	 * Set autoFocus on input element
	 */ autoFocus: PropTypes.bool,
    /**
	 * Please see https://material-ui.com/api/autocomplete/
	 */ blurOnSelect: PropTypes.string,
    /**
	 * If true, select will have clear button and event
	 */ clearable: PropTypes.bool,
    /**
	 * Custom `className` for the control component.
	 */ controlClassName: PropTypes.string,
    /**
	 * Custom `className` for the control input component.
	 */ controlInputClassName: PropTypes.string,
    /**
	 * controlPanelIconClose
	 */ controlPanelIconClose: PropTypes.node,
    /**
	 * controlPanelIconOpen
	 */ controlPanelIconOpen: PropTypes.node,
    /**
	 * If true, component will be disabled
	 */ disabled: PropTypes.bool,
    /**
	 * Metada for validations errors
	 */ errMeta: PropTypes.arrayOf(PropTypes.shape({
        errorMessage: PropTypes.string,
        index: PropTypes.number
    })),
    /**
	 * Options filter function
	 */ filterOptions: PropTypes.bool,
    /**
	 * Please see https://material-ui.com/api/autocomplete/
	 */ filterSelectedOptions: PropTypes.any,
    /**
	 * Custom `className` for the floating label.
	 */ floatingLabelClassName: PropTypes.string,
    /**
	 * Floating label will be displayed inside of component
	 */ floatingLabelText: PropTypes.node,
    /**
	 * Free text variant
	 */ freeSolo: PropTypes.bool,
    /**
	 * Please see https://material-ui.com/api/autocomplete/
	 */ getOptionDisabled: PropTypes.bool,
    /**
	 * Please see https://material-ui.com/api/autocomplete/
	 */ getOptionLabel: PropTypes.func,
    getOptionSelected: PropTypes.func,
    /**
	 * Please see https://material-ui.com/api/autocomplete/
	 */ groupBy: PropTypes.string,
    /**
	 * Id of element
	 */ id: PropTypes.string,
    /**
	 * Raw value of input
	 */ inputValue: PropTypes.string,
    /**
	 * Custom `className` for the label.
	 */ labelClassName: PropTypes.string,
    /**
	 * LabelNote for the `TextField` component
	 */ labelNote: PropTypes.node,
    /**
	 * Position of label (`left`, `right`)
	 */ labelPosition: PropTypes.oneOf(FormGroup.labelPositions.values()),
    /**
	 * Text of label
	 */ labelText: PropTypes.node,
    /**
	 * HTML <input> maxlength attribute
	 */ maxLength: PropTypes.string,
    /**
	 * If true, select will be multiple
	 */ multiple: PropTypes.bool,
    /**
	 * No options text
	 */ noOptionsText: PropTypes.node,
    /**
	 * On blur callback
	 *
	 * @param {Object} event - event targeting the element
	 * @return {undefined} Nothing
	 */ onBlur: PropTypes.func,
    /**
	 * On change callback
	 *
	 * @param {Object} event - event targeting the element
	 * @param {String} newValue - new value of select
	 * @return {undefined} Nothing
	 */ onChange: PropTypes.func,
    /**
	 * Please see https://material-ui.com/api/autocomplete/
	 */ onClose: PropTypes.func,
    /**
	 * On focus callback
	 *
	 * @param {Object} event event targeting the element
	 * @return {undefined} Nothing
	 */ onFocus: PropTypes.func,
    /**
	 * On input change callback
	 *
	 * @param {Object} event - event targeting the element
	 * @param {String} newValue - new value of select
	 * @return {undefined} Nothing
	 */ onInputChange: PropTypes.func,
    /**
	 * Please see https://material-ui.com/api/autocomplete/
	 */ onOpen: PropTypes.func,
    /**
	 * Please see https://material-ui.com/api/autocomplete/
	 */ openOnFocus: PropTypes.bool,
    /**
	 * Options for autocomplete
	 */ options: PropTypes.array,
    /**
	 * Element which will be used for rendering select options.
	 * By default, it uses the body of the top-level document object, so it's simply document.body
	 */ optionsTargetContainer: PropTypes.oneOfType([
        PropTypes.instanceOf(Element),
        PropTypes.element
    ]),
    /**
	 * Whether to use outlined version of Input
	 */ outline: PropTypes.bool,
    /**
	 * Text of placeholder
	 */ placeholder: PropTypes.string,
    /**
	 * Component is read only
	 */ readOnly: PropTypes.bool,
    /**
	 * Input of select is read only
	 */ readOnlyInput: PropTypes.bool,
    /**
	 * Custom option render
	 */ renderOption: PropTypes.func,
    /**
	 * Show whether Component is required
	 */ required: PropTypes.bool,
    /**
	 * Please see https://material-ui.com/api/autocomplete/
	 */ selectOnFocus: PropTypes.bool,
    /**
	 * If true, Select will be rendered with sizeSmall modifier
	 */ sizeSmall: PropTypes.bool,
    /**
	 * Input's tabindex.
	 */ tabIndex: PropTypes.string,
    /**
	 * Tooltip children, either string or custom element
	 */ tooltip: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.element
    ]),
    /**
	 * Custom `className` for validation.
	 */ validationClassName: PropTypes.string,
    /**
	 * Where to display validation
	 */ validationDisplayType: validationDisplayTypePropType,
    /**
	 * Text that will be shown when withValidity decorator decides to show validation text.
	 */ validationText: PropTypes.node,
    /**
	 * Value
	 */ value: PropTypes.any
};
export default MuiAutocomplete;
